import { revalidateLogic, useForm } from '@tanstack/react-form';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AreaConfigs,
  GlobalConfig,
  ProductionStreamConfigData,
  ScenarioFormData,
} from './types';
import { scenarioFormDefaultValues } from './default-values';
import {
  onChangeValidation,
  onDynamicValidation,
  getSectionsWithErrors,
} from './validations';
import { FormErrorsProvider } from 'components/form-fields-2';
import { AreaConfigResponse } from 'types/dca-scenarios-api/responses';
import { useSetAtom } from 'jotai';
import {
  dcaScenarioDataAtom,
  deleteMultipleProductionStreamConfigsAtom,
  saveAreaConfigAtom,
  saveDcaScenarioAtom,
  updateDcaScenarioAtom,
} from './atoms';
import { isEqual } from 'lodash-es';
import {
  getChangedObject,
  isGeneralConfigModified,
  isSegmentationConfigModified,
  isOutlierConfigModified,
  isProductionStreamConfigModified,
} from './utils';
import { useHistory } from 'react-router-dom';

export type SetFieldBaselineFn = (
  field: keyof ScenarioFormData,
  value: ScenarioFormData[keyof ScenarioFormData]
) => void;

export type UpdateFieldDefaultFn = (
  field: keyof ScenarioFormData,
  value: ScenarioFormData[keyof ScenarioFormData]
) => void;

export interface ConfigData extends AreaConfigResponse {
  type: 'global' | 'area';
}

type ScenarioFormInstance = ReturnType<typeof useCreateScenarioForm>;

interface ScenarioFormContextType {
  form: ReturnType<typeof useCreateScenarioForm> | null;
  defaultValues: ScenarioFormData | null;
  /** Snapshot of each field from the last BE load or save. Use for top-level dirty detection (e.g. save button). Only updated by setFieldBaseline, never by updateFieldDefault. */
  baselines: ScenarioFormData | null;
  /** Sets form value AND baseline for a top-level field. After calling, the field is "clean" (not dirty). Use when loading from BE or after a successful save. */
  setFieldBaseline: SetFieldBaselineFn | null;
  /** Updates only the baseline (defaults) without changing form values. Use for structural array changes (duplicate/delete) where you want to update what "clean" means without resetting modified elements. */
  updateFieldDefault: UpdateFieldDefaultFn | null;
  /** Snapshots current form values as the new baselines/defaults, making the form "clean". Call after a successful save/update. */
  resetForm: (() => void) | null;
  /** Reverts all form fields back to their baseline values, discarding unsaved changes. */
  discardFormChanges: (() => void) | null;
  /** Returns true if any form field differs from its baseline. */
  isFormModified: ((values: ScenarioFormData) => boolean) | null;
  configs: ConfigData[];
  setConfigs: React.Dispatch<React.SetStateAction<ConfigData[]>>;
  selectedConfig: ConfigData | null;
  resetConfigId: (id: string) => void;
  setSelectedConfigId: React.Dispatch<React.SetStateAction<string | null>>;
  handleSubmitForm: (meta: ScenarioFormSubmitMeta) => Promise<void>;
  discardCount: number;
  validConfigState: {
    isValid: boolean;
    message: string;
  } | null;
  setValidConfigState: React.Dispatch<
    React.SetStateAction<{
      isValid: boolean;
      message: string;
    } | null>
  >;
  setEnablePopulateConfig: React.Dispatch<React.SetStateAction<boolean>>;
  saveCount: number;
  setDiscardCount: React.Dispatch<React.SetStateAction<number>>;
}

export const ScenarioFormContext = createContext<ScenarioFormContextType>({
  form: null,
  defaultValues: null,
  baselines: null,
  setFieldBaseline: null,
  updateFieldDefault: null,
  resetForm: null,
  discardFormChanges: null,
  isFormModified: null,
  configs: [],
  setConfigs: () => {},
  selectedConfig: null,
  resetConfigId: () => {},
  setSelectedConfigId: () => {},
  handleSubmitForm: () => Promise.resolve(),
  discardCount: 0,
  validConfigState: null,
  setValidConfigState: () => {},
  setEnablePopulateConfig: () => {},
  saveCount: 0,
  setDiscardCount: () => {},
});

type ScenarioFormSubmitMeta = {
  dcaScenarioId: string | null;
  areaConfigId: string | null;
};
const useCreateScenarioForm = (defaultValues: ScenarioFormData) => {
  return useForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'change',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onChange: ({ value }: { value: ScenarioFormData }) =>
        onChangeValidation(value),
      onDynamic: ({ value }: { value: ScenarioFormData }) =>
        onDynamicValidation(value),
    },
  });
};

interface ScenarioFormProviderProps {
  commentsUrl?: string;
  eventsUrl?: string;
  areaConfigsUrl?: string;
  children: React.ReactNode;
}

const INITIAL_VALUES = {
  globalConfig: scenarioFormDefaultValues.globalConfig,
  areaConfigs: null,
  generalConfig: null,
  segmentationConfig: null,
  outlierConfig: null,
  oilStreamConfigs: [],
  gasStreamConfigs: [],
  waterStreamConfigs: [],
};

export const ScenarioFormProvider = ({
  children,
}: ScenarioFormProviderProps) => {
  const [defaultValues, setDefaultValues] = useState<ScenarioFormData>(
    INITIAL_VALUES as ScenarioFormData
  );

  const [baselines, setBaselines] = useState<ScenarioFormData>(
    INITIAL_VALUES as ScenarioFormData
  );

  const [validConfigState, setValidConfigState] = useState<{
    isValid: boolean;
    message: string;
  } | null>(null);

  const [discardCount, setDiscardCount] = useState(0);
  const [saveCount, setSaveCount] = useState(0);

  const history = useHistory();

  const updateDcaScenario = useSetAtom(updateDcaScenarioAtom);
  const saveDcaScenario = useSetAtom(saveDcaScenarioAtom);
  const saveAreaConfig = useSetAtom(saveAreaConfigAtom);
  const setDcaScenarioDataState = useSetAtom(dcaScenarioDataAtom);
  const deleteMultipleProductionStreamConfigs = useSetAtom(
    deleteMultipleProductionStreamConfigsAtom
  );

  const [configs, setConfigs] = useState<ConfigData[]>([]);
  const [enablePopulateConfig, setEnablePopulateConfig] = useState(false);

  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);

  const form = useCreateScenarioForm(defaultValues);

  const setFieldBaseline = useCallback<SetFieldBaselineFn>(
    (field, value) => {
      form.setFieldValue(field, value);
      setDefaultValues((prev) => ({ ...prev, [field]: value }));
      setBaselines((prev) => ({ ...prev, [field]: value }));
    },
    [form]
  );

  const updateFieldDefault = useCallback<UpdateFieldDefaultFn>(
    (field, value) => {
      setDefaultValues((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetForm = useCallback(() => {
    const currentValues = { ...form.state.values };
    setDefaultValues(currentValues);
    setBaselines(currentValues);
  }, [form]);

  useEffect(() => {
    if (!selectedConfigId) return;
    setValidConfigState(null);
  }, [selectedConfigId, setValidConfigState]);

  const discardFormChanges = useCallback(() => {
    form.setFieldValue('globalConfig', baselines.globalConfig);
    setFieldBaseline('areaConfigs', baselines.areaConfigs);

    const baselineArea = baselines.areaConfigs;
    if (baselineArea) {
      setConfigs((prev) =>
        prev.map((config) => {
          return config?.id?.toString() === selectedConfigId
            ? {
                ...config,
                areaGeometryId: baselineArea.areaGeometryId,
                developmentType: baselineArea.developmentType,
                formation: baselineArea.formation,
                areaGeometryName: baselineArea.areaGeometryName,
              }
            : config;
        })
      );
    }

    setFieldBaseline('generalConfig', baselines.generalConfig);
    setFieldBaseline('segmentationConfig', baselines.segmentationConfig);
    setFieldBaseline('outlierConfig', baselines.outlierConfig);
    setFieldBaseline('oilStreamConfigs', baselines.oilStreamConfigs);
    setFieldBaseline('gasStreamConfigs', baselines.gasStreamConfigs);
    setFieldBaseline('waterStreamConfigs', baselines.waterStreamConfigs);
    setDiscardCount((prev) => prev + 1);
  }, [
    form,
    baselines,
    setFieldBaseline,
    selectedConfigId,
    setDiscardCount,
    setConfigs,
  ]);

  const isFormModified = useCallback(
    (values: ScenarioFormData) => {
      // commented out for now to avoid console.log pollution
      // console.log({
      //   globalConfig: !!values.globalConfig &&
      //   !isEqual(values.globalConfig, baselines.globalConfig),
      //   areaConfigs: !!values.areaConfigs &&
      //   !isEqual(values.areaConfigs, baselines.areaConfigs),
      //   generalConfig: !isEqual(values.generalConfig, baselines.generalConfig),
      //   segmentationConfig: !isEqual(values.segmentationConfig, baselines.segmentationConfig),
      //   outlierConfig: !isEqual(values.outlierConfig, baselines.outlierConfig),
      //   oilStreamConfigs: !!values.oilStreamConfigs &&
      //   values.oilStreamConfigs.length > 0 &&
      //   !isEqual(values.oilStreamConfigs, baselines.oilStreamConfigs),
      //   gasStreamConfigs: !!values.gasStreamConfigs &&
      //   values.gasStreamConfigs.length > 0 &&
      //   !isEqual(values.gasStreamConfigs, baselines.gasStreamConfigs),
      //   waterStreamConfigs: !!values.waterStreamConfigs &&
      //   values.waterStreamConfigs.length > 0 &&
      //   !isEqual(values.waterStreamConfigs, baselines.waterStreamConfigs),
      // }, 'modified')

      const isStreamModified = (
        current: ProductionStreamConfigData[] | null,
        baseline: ProductionStreamConfigData[] | null
      ): boolean => {
        if (!current || current.length === 0) return false;
        if (!baseline || current.length !== baseline.length) return true;
        return current.some((config, i) =>
          isProductionStreamConfigModified(config, baseline[i])
        );
      };

      return (
        (!!values.globalConfig &&
          !isEqual(values.globalConfig, baselines.globalConfig)) ||
        (!!values.areaConfigs &&
          !isEqual(values.areaConfigs, baselines.areaConfigs)) ||
        (!!values.generalConfig &&
          !!baselines.generalConfig &&
          isGeneralConfigModified(
            values.generalConfig,
            baselines.generalConfig
          )) ||
        (!!values.segmentationConfig &&
          !!baselines.segmentationConfig &&
          isSegmentationConfigModified(
            values.segmentationConfig,
            baselines.segmentationConfig
          )) ||
        (!!values.outlierConfig &&
          !!baselines.outlierConfig &&
          isOutlierConfigModified(
            values.outlierConfig,
            baselines.outlierConfig
          )) ||
        isStreamModified(values.oilStreamConfigs, baselines.oilStreamConfigs) ||
        isStreamModified(values.gasStreamConfigs, baselines.gasStreamConfigs) ||
        isStreamModified(
          values.waterStreamConfigs,
          baselines.waterStreamConfigs
        )
      );
    },
    [baselines]
  );

  const handleSubmitForm = useCallback(
    async (meta: ScenarioFormSubmitMeta) => {
      const { values } = form.state;

      const insertPayload = {
        globalConfig: values.globalConfig as GlobalConfig,
        areaConfig: values.areaConfigs as AreaConfigs,
        generalConfigId: values.generalConfig?.id as string,
        segmentationConfigId: values.segmentationConfig?.id as string,
        outlierConfigId: values.outlierConfig?.id as string,
        productionStreamConfigs: [
          values.oilStreamConfigs,
          values.gasStreamConfigs,
          values.waterStreamConfigs,
        ]
          .flat()
          .filter((config): config is ProductionStreamConfigData => !!config),
      };

      const updatePayload = {
        globalConfig: getChangedObject(
          values.globalConfig,
          baselines.globalConfig
        ) as Partial<GlobalConfig>,
        areaConfig: getChangedObject(
          values.areaConfigs,
          baselines.areaConfigs
        ) as Partial<AreaConfigs>,
        generalConfigId:
          form.state.values.generalConfig?.id !== baselines.generalConfig?.id
            ? (form.state.values.generalConfig?.id ?? null)
            : null,
        segmentationConfigId:
          form.state.values.segmentationConfig?.id !==
          baselines.segmentationConfig?.id
            ? (form.state.values.segmentationConfig?.id ?? null)
            : null,
        outlierConfigId:
          form.state.values.outlierConfig?.id !== baselines.outlierConfig?.id
            ? (form.state.values.outlierConfig?.id ?? null)
            : null,
        productionStreamConfigs: [
          ...(values.oilStreamConfigs || []).map((config) => {
            return config.id
              ? getChangedObject(
                  config,
                  baselines.oilStreamConfigs?.find((c) => c.id === config.id) ??
                    null,
                  true
                )
              : config;
          }),
          ...(values.gasStreamConfigs || []).map((config) => {
            return config.id
              ? getChangedObject(
                  config,
                  baselines.gasStreamConfigs?.find((c) => c.id === config.id) ??
                    null,
                  true
                )
              : config;
          }),
          ...(values.waterStreamConfigs || []).map((config) => {
            return config.id
              ? getChangedObject(
                  config,
                  baselines.waterStreamConfigs?.find(
                    (c) => c.id === config.id
                  ) ?? null,
                  true
                )
              : config;
          }),
        ].filter(
          (config) => Object.keys(config).length > 0
        ) as Partial<ProductionStreamConfigData>[],
      };

      if (meta.dcaScenarioId) {
        if (meta.areaConfigId) {
          // update dca scenario and area config
          await updateDcaScenario({
            payload: {
              ...updatePayload,
              areaConfigId: meta.areaConfigId!,
              dcaScenarioId: meta.dcaScenarioId!,
            },
            onSuccess: ({ areaConfig, productionStreamConfigs }) => {
              let oilStreamConfigs = productionStreamConfigs.filter(
                (config) => config.stream === 'oil'
              );
              let gasStreamConfigs = productionStreamConfigs.filter(
                (config) => config.stream === 'gas'
              );
              let waterStreamConfigs = productionStreamConfigs.filter(
                (config) => config.stream === 'water'
              );

              if (areaConfig) {
                setConfigs((prev) =>
                  prev.map((config) => {
                    return config.id === areaConfig.id
                      ? {
                          ...areaConfig,
                          type:
                            areaConfig.areaGeometryId === null
                              ? 'global'
                              : 'area',
                        }
                      : config;
                  })
                );
              }

              if (enablePopulateConfig) {
                const existingOilStreamConfigs =
                  baselines.oilStreamConfigs as ProductionStreamConfigData[];
                const existingGasStreamConfigs =
                  baselines.gasStreamConfigs as ProductionStreamConfigData[];
                const existingWaterStreamConfigs =
                  baselines.waterStreamConfigs as ProductionStreamConfigData[];

                const existingProductionStreamConfigUrls = [
                  ...existingOilStreamConfigs,
                  ...existingGasStreamConfigs,
                  ...existingWaterStreamConfigs,
                ]
                  .filter((config) => config && config.href)
                  .map((config) => config.href!);

                if (existingProductionStreamConfigUrls.length > 0) {
                  oilStreamConfigs = oilStreamConfigs.filter(
                    (config) =>
                      !existingProductionStreamConfigUrls.includes(config.href!)
                  );
                  gasStreamConfigs = gasStreamConfigs.filter(
                    (config) =>
                      !existingProductionStreamConfigUrls.includes(config.href!)
                  );
                  waterStreamConfigs = waterStreamConfigs.filter(
                    (config) =>
                      !existingProductionStreamConfigUrls.includes(config.href!)
                  );

                  deleteMultipleProductionStreamConfigs({
                    urls: existingProductionStreamConfigUrls,
                  });
                }

                setEnablePopulateConfig(false);
              }

              const currentValues = {
                ...form.state.values,
                oilStreamConfigs,
                gasStreamConfigs,
                waterStreamConfigs,
              };

              form.setFieldValue('oilStreamConfigs', oilStreamConfigs);
              form.setFieldValue('gasStreamConfigs', gasStreamConfigs);
              form.setFieldValue('waterStreamConfigs', waterStreamConfigs);

              setDefaultValues(currentValues);
              setBaselines(currentValues);
            },
          });
        } else {
          const globalConfigs = configs.filter(
            (config) => config.type === 'global'
          );
          if (
            globalConfigs.length >= 2 &&
            insertPayload.areaConfig.areaGeometryId === null
          ) {
            setDcaScenarioDataState({
              loading: false,
              error: 'No more global configs allowed',
            });

            return;
          }

          // inset new area config for existing dca scenario
          await saveAreaConfig({
            payload: { ...insertPayload, dcaScenarioId: meta.dcaScenarioId! },
            onSuccess: ({ areaConfig, productionStreamConfigs }) => {
              const oilStreamConfigs = productionStreamConfigs.filter(
                (config) => config.stream === 'oil'
              );
              const gasStreamConfigs = productionStreamConfigs.filter(
                (config) => config.stream === 'gas'
              );
              const waterStreamConfigs = productionStreamConfigs.filter(
                (config) => config.stream === 'water'
              );

              const currentValues = {
                ...form.state.values,
                oilStreamConfigs,
                gasStreamConfigs,
                waterStreamConfigs,
              };

              form.setFieldValue('oilStreamConfigs', oilStreamConfigs);
              form.setFieldValue('gasStreamConfigs', gasStreamConfigs);
              form.setFieldValue('waterStreamConfigs', waterStreamConfigs);

              setDefaultValues(currentValues);
              setBaselines(currentValues);
              setConfigs((prev) =>
                prev.map((config) => {
                  return config.id.toString() === selectedConfigId
                    ? {
                        ...areaConfig,
                        type:
                          areaConfig.areaGeometryId === null
                            ? 'global'
                            : 'area',
                      }
                    : config;
                })
              );
              setSelectedConfigId(areaConfig.id);
            },
          });
        }
      } else {
        // insert new dca scenario
        await saveDcaScenario({
          payload: insertPayload,
          onSuccess: (data) => {
            history.replace(`/dca_scenarios/${data.dcaScenarioId}`);
          },
        });
      }

      setSaveCount((prev) => prev + 1);
    },
    [
      form,
      baselines,
      selectedConfigId,
      configs,
      history,
      updateDcaScenario,
      saveDcaScenario,
      saveAreaConfig,
      setDcaScenarioDataState,
      enablePopulateConfig,
      deleteMultipleProductionStreamConfigs,
      setEnablePopulateConfig,
    ]
  );

  const resetConfigId = useCallback(
    (id: string) => {
      if (selectedConfigId === id) return;
      setSelectedConfigId(id);
      form.setFieldValue('areaConfigs', null);
      form.setFieldValue('generalConfig', null);
      form.setFieldValue('segmentationConfig', null);
      form.setFieldValue('outlierConfig', null);
      form.setFieldValue('oilStreamConfigs', []);
      form.setFieldValue('gasStreamConfigs', []);
      form.setFieldValue('waterStreamConfigs', []);
    },
    [form, selectedConfigId]
  );

  const selectedConfig: ConfigData | null = useMemo(() => {
    return configs.find((config) => config.id === selectedConfigId) || null;
  }, [configs, selectedConfigId]);

  return (
    <ScenarioFormContext.Provider
      value={{
        form,
        defaultValues,
        baselines,
        setFieldBaseline,
        updateFieldDefault,
        resetForm,
        discardFormChanges,
        isFormModified,
        configs,
        setConfigs,
        selectedConfig,
        resetConfigId,
        setSelectedConfigId,
        handleSubmitForm,
        discardCount,
        validConfigState,
        setValidConfigState,
        setEnablePopulateConfig,
        saveCount,
        setDiscardCount,
      }}
    >
      {children}
    </ScenarioFormContext.Provider>
  );
};

const FormErrorsMerger = ({
  errorMap,
  children,
}: {
  errorMap: Record<string, unknown>;
  children: React.ReactNode;
}) => {
  const contextValue = useMemo(() => {
    const onChange = errorMap.onChange;
    const onDynamic = errorMap.onDynamic;

    const messages: string[] = [];
    if (typeof onChange === 'string' && onChange) messages.push(onChange);
    if (typeof onDynamic === 'string' && onDynamic) messages.push(onDynamic);

    const combined = Array.from(
      new Set(messages.flatMap((m) => m.split(', ')))
    ).join(', ');
    const sectionsWithErrors = getSectionsWithErrors(combined);

    return { formMessage: combined, sectionsWithErrors };
  }, [errorMap.onChange, errorMap.onDynamic]);

  return (
    <FormErrorsProvider value={contextValue}>{children}</FormErrorsProvider>
  );
};

export const FormErrorsBridge = ({
  form,
  children,
}: {
  form: ScenarioFormInstance;
  children: React.ReactNode;
}) => (
  <form.Subscribe selector={(state) => state.errorMap}>
    {(errorMap: any) => (
      <FormErrorsMerger errorMap={errorMap}>{children}</FormErrorsMerger>
    )}
  </form.Subscribe>
);

export const useScenarioForm = () => {
  const context = useContext(ScenarioFormContext);
  if (
    !context?.form ||
    context.defaultValues === null ||
    context.baselines === null ||
    !context.setFieldBaseline ||
    !context.updateFieldDefault ||
    !context.resetForm ||
    !context.discardFormChanges ||
    !context.isFormModified
  ) {
    throw new Error(
      'useScenarioForm must be used within a ScenarioFormProvider'
    );
  }
  return context as Omit<
    ScenarioFormContextType,
    | 'form'
    | 'defaultValues'
    | 'baselines'
    | 'setFieldBaseline'
    | 'updateFieldDefault'
    | 'resetForm'
    | 'discardFormChanges'
    | 'isFormModified'
    | 'discardCount'
    | 'setDiscardCount'
  > & {
    form: ScenarioFormInstance;
    defaultValues: ScenarioFormData;
    baselines: ScenarioFormData;
    setFieldBaseline: SetFieldBaselineFn;
    updateFieldDefault: UpdateFieldDefaultFn;
    resetForm: () => void;
    discardFormChanges: () => void;
    isFormModified: (values: ScenarioFormData) => boolean;
    discardCount: number;
    setDiscardCount: React.Dispatch<React.SetStateAction<number>>;
  };
};
